<template>
  <div>
    <hr class="mt-2" />
    <div class="px-4 pb-2">
      <template v-if="estimation && isMitrex">
        <div class="is-size-6 mb-1 has-text-weight-bold">
          Project City and Electricity Rate
        </div>
        <div class="columns is-multiline">
          <div class="column is-2">
            <label class="help">City</label>
            <v-select
              class="mt-1"
              required
              :disabled="!editable"
              :options="cities"
              item-text="city"
              item-value="city"
              v-model="estimation.city"
              @input="updateCity"
            />
          </div>
          <div class="column is-2" v-if="estimation.insulation">
            <label class="help">Electricity Rate</label>
            <v-text-field
              class="mt-1 field"
              required
              :disabled="!editable"
              type="number"
              v-model="estimation.insulation.elec_fuel_cost"
              @blur="updateInsulationParam(
                estimation.insulation.id,
                'elec_fuel_cost',
                $event,
                false
              )"
              @keyup.enter.native="updateInsulationParam(
                estimation.insulation.id, 
                'elec_fuel_cost', 
                estimation.insulation.elec_fuel_cost, 
                false
              )"
              has-addons
            >
              <p class="control">
                <a class="button is-static">
                  $ / KWH
                </a>
              </p>
            </v-text-field>
          </div>
        </div>

        <template v-if="estimation.manual_sun_hours_obj">
          <div class="is-size-6 mb-1 has-text-weight-bold">
            Average Yearly Hours of Sun
          </div>
          <div class="columns">
            <template v-for="elevation in Object.keys(estimation.manual_sun_hours_obj)">
              <div class="column" :key="elevation">
                <label class="help capitalize mb-1">
                  {{ elevation.replaceAll("_", " ") }}
                </label>
                <v-text-field
                  type="number"
                  required
                  :disabled="!editable"
                  v-model="estimation.manual_sun_hours_obj[elevation]"
                  @blur="update('manual_sun_hours_obj', estimation.manual_sun_hours_obj, false, false)"
                  @keyup.enter.native="update('manual_sun_hours_obj', estimation.manual_sun_hours_obj, false, false)"
                />
              </div>
            </template>
          </div>
        </template>

        <hr class="my-3" />
      </template>

      <div v-if="newElevations">
        <estimation-condensed-item
          v-for="item in estimation.items" 
          :key="item.id"
          :estimation="estimation"
          :item="item"
          :material="getProduct(item)"
          :architectureName="item.architecture_name"
          :editable="editable"
          :sunHourDict="sunHourDict"
          :elevations="newElevations"
        />
      </div>
    </div>
  </div>
</template>

<script>
import VSelect from '@/components/base/VSelect'
import VTextField from '@/components/base/VTextField'
import EstimationCondensedItem from '@/components/estimations/EstimationCondensedItem'
import { sunHourFields } from '@/data/estimation-data'
import { citySunHours } from '@/data/city-sun-hours'
import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    editable: {
      type: Boolean
    },
    update: {
      type: Function
    },
    updateInsulationParam: {
      type: Function
    }
  },
  components: { VSelect, VTextField, EstimationCondensedItem },
  data() {
    return {
      cities: citySunHours,
      sunHourFields,
      sunHourDict: {
        'north': 'sunlight_n',
        'south': 'sunlight_s',
        'east': 'sunlight_e',
        'west': 'sunlight_w',
        'n/ east': 'sunlight_ne',
        'n/ west': 'sunlight_nw',
        's/ west': 'sunlight_sw',
        's/ east': 'sunlight_se',
      }
    }
  },
  computed: {
    ...mapState("cladifyEstimation", [
      "estimation", "loading", "firstLoad"
    ]),
    ...mapState("elevations", {
      newElevations: "elevations"
    }),
    elevationsNotEqual({ newElevations, estimation }) {
      const newElevationNames = newElevations.map(e => e.elevation_name.toLowerCase())
      const currElevationNames = Object.keys(estimation.manual_sun_hours_obj)
      return newElevationNames.length !== currElevationNames.length ||
        !newElevationNames.every(e => currElevationNames.includes(e)) ||
        ! currElevationNames.every(e => newElevationNames.includes(e))
    },
    isMitrex({ estimation }) {
      return estimation.rfq.product_brand === 'Mitrex';
    }
  },
  watch: {
    newElevations: {
      handler() {
        if (!this.isMitrex && this.elevationsNotEqual) {
          this.syncSunHours()
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions("elevations", ["getElevations"]),
    ...mapMutations("rfq", ["setRfq"]),
    initializeEstimation() {
      this.setRfq(this.estimation.rfq);
      this.getElevations().then(() => {
        if (this.isMitrex) {
          this.checkSunHours();
        }
      })
    },
    checkSunHours() {
      if (!this.estimation.manual_sun_hours_obj) {
        const sunHours = Object.fromEntries(this.newElevations.map(e => {
          return [e.elevation_name.toLowerCase(), e.default_sun_hours]
        }))
        this.update('manual_sun_hours_obj', sunHours, false, false)
      } else if (this.elevationsNotEqual) {
        this.syncSunHours()
      }
    },
    syncSunHours() {
      const currElevations = Object.keys(this.estimation.manual_sun_hours_obj)
      this.newElevations.forEach(e => {
        if (!currElevations.includes(e.elevation_name.toLowerCase())) {
          this.estimation.manual_sun_hours_obj[e.elevation_name.toLowerCase()] =
            e.default_sun_hours
        }
      })
      currElevations.forEach(e => {
        if (!this.newElevations.map(e => e.elevation_name.toLowerCase()).includes(e)) {
          delete this.estimation.manual_sun_hours_obj[e]
        }
      })
      this.update('manual_sun_hours_obj', this.estimation.manual_sun_hours_obj, false, false);
    },
    updateCity(city) {
      if (city){
        const citySelected = this.cities.find(c => c.city === city);
        const sunHours = Object.fromEntries(this.newElevations.map(e => {
          const elevationName = e.elevation_name.toLowerCase();
          const elevationValue = citySelected[elevationName] 
            ? citySelected[elevationName]
            : e.default_sun_hours
          return [elevationName, elevationValue]
        }))
        this.update('city', city, false, false);
        this.update('manual_sun_hours_obj', sunHours, false, false)
        const { estimation } = this;
        if (citySelected.elec_cost_per_kwh && estimation.insulation) {
          this.updateInsulationParam(
            estimation.insulation.id, 
            'elec_fuel_cost', 
            citySelected.elec_cost_per_kwh, 
            false
          )
        }
      }
    },
    getProduct(item) {
      return item.materials.find(m => 
        [13, 20].includes(m?.product_master?.product_category_id)
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/flexbox.scss';
.capitalize {
  text-transform: capitalize;
}
</style>